/* eslint-disable unicorn/no-null */
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// mantine
import { ActionIcon, Box, Button, Group, Menu } from '@mantine/core';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { useDisclosure } from '@mantine/hooks';
// icons
import { EllipsisVertical, Plus } from 'lucide-react';
// components
import type {
    TCustomerLink,
    TProjectVersion,
    TV3CreateProjectData,
    TV3Project,
    TV3UpdateProjectData
} from '@newo/api/gateway';
import Drawer from '@/components/Drawer';
import ProjectForm from '@/pages/Projects/components/ProjectForm';
import Header from '@/components/Layout/components/Header';
import DateTime from '@/modules/DateTime/components/DateTime';
import ProjectMenu from '@/pages/Projects/components/ProjectMenu';
import { ActiveVersionBadge } from '@/pages/Project/components/ActiveVersionIndicator/ActiveVersionBadge';
// utils
import { useProjectsV2 } from '@/pages/Projects/Projects.hooks';
import { useAllProjectVersionsQuery } from '@/pages/Project/components/Files/ProjectVersions.queries';
import { useCustomerLinksQuery } from '@/pages/Project/components/Files/CustomerLinks.queries';
import { PATHS } from '@/const';
import { PageContainer } from '@/components/PageContainer/PageContainer';

const Projects: FC = () => {
    const [selectedProject, setSelectedProject] = useState<
        TV3Project | undefined
    >();
    const [isDrawerOpened, handlers] = useDisclosure(false);

    const {
        projects,
        isProjectsLoading,
        onProjectCreate,
        onProjectDelete,
        onProjectUpdate,
        onProjectForceUpdate
    } = useProjectsV2();

    const { data: customerLinks = [] } = useCustomerLinksQuery();
    const { data: allVersions = [] } = useAllProjectVersionsQuery();

    const linkByProjectIdn = useMemo(
        () =>
            new Map<string, TCustomerLink>(
                customerLinks.map((link) => [link.project_idn, link])
            ),
        [customerLinks]
    );

    const versionById = useMemo(
        () =>
            new Map<string, TProjectVersion>(
                allVersions.map((version) => [version.id, version])
            ),
        [allVersions]
    );

    const handleProjectDrawerOpen = (project?: TV3Project) => {
        setSelectedProject(project);
        handlers.open();
    };

    const handleProjectDrawerClose = () => {
        setSelectedProject(undefined);
        handlers.close();
    };

    const handleForceUpdate = async (project: TV3Project) => {
        await onProjectForceUpdate({
            projectIdn: project.idn
        });
    };

    const handleSubmit = async (
        data: TV3CreateProjectData | TV3UpdateProjectData,
        projectId?: string
    ) => {
        await (projectId
            ? onProjectUpdate({ data, projectId })
            : onProjectCreate(data as TV3CreateProjectData));
        handleProjectDrawerClose();
    };

    const columns: DataTableColumn<TV3Project>[] = [
        {
            title: 'Idn',
            accessor: 'idn',
            cellsClassName: 'truncate',
            width: 200
        },
        {
            title: 'Title',
            accessor: 'title',
            cellsClassName: 'break-all',
            width: 200
        },
        {
            title: 'Description',
            accessor: 'description'
        },
        {
            title: 'Active Version',
            accessor: 'id',
            width: 240,
            render: (project) => {
                const link = linkByProjectIdn.get(project.idn) ?? null;
                const version = link?.project_version_id
                    ? (versionById.get(link.project_version_id) ?? null)
                    : null;
                return <ActiveVersionBadge link={link} version={version} />;
            }
        },
        {
            title: 'Created At',
            accessor: 'created_at',
            width: 200,
            render: (project) => {
                return (
                    <DateTime
                        format="yyyy-LL-dd HH:mm a"
                        value={project.created_at}
                    />
                );
            }
        },
        {
            title: 'Updated At',
            accessor: 'updated_at',
            width: 200,
            render: (project) => {
                return (
                    <DateTime
                        format="yyyy-LL-dd HH:mm a"
                        value={project.updated_at}
                    />
                );
            }
        },
        {
            title: 'Actions',
            accessor: 'actions',
            width: 150,
            render: (project) => {
                return (
                    <Group gap="xs">
                        <Button
                            component={Link}
                            size="xs"
                            to={`${PATHS.projects}/${project.id}`}
                        >
                            Manage
                        </Button>
                        <ProjectMenu
                            project={project}
                            onDeleteProject={onProjectDelete}
                            onEditProject={handleProjectDrawerOpen}
                            onForceUpdateProject={handleForceUpdate}
                        />
                    </Group>
                );
            }
        }
    ];

    return (
        <Box className="flex flex-col h-full">
            <Helmet>
                <title>NEWO - Projects</title>
            </Helmet>
            <Header
                isSearchVisible
                breadcrumbs={[{ label: 'Projects' }]}
                isPublishAllVisible={false}
            >
                <Menu>
                    <Menu.Target>
                        <ActionIcon variant="transparent">
                            <EllipsisVertical />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            leftSection={<Plus />}
                            onClick={() => {
                                handleProjectDrawerOpen();
                            }}
                        >
                            Create New Project
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Header>
            <PageContainer className="flex-1 min-h-0 flex flex-col pt-6">
                <DataTable
                    highlightOnHover
                    pinLastColumn
                    columns={columns}
                    fetching={isProjectsLoading}
                    height="85vh"
                    idAccessor="id"
                    loaderBackgroundBlur={1}
                    loaderColor="dark"
                    records={projects}
                    scrollAreaProps={{
                        scrollbarSize: 10,
                        type: 'always'
                    }}
                    verticalAlign="top"
                />
            </PageContainer>
            <Drawer
                name="project-form"
                opened={isDrawerOpened}
                position="right"
                size="sm"
                title={selectedProject ? 'Edit Project' : 'Create Project'}
                onClose={handleProjectDrawerClose}
            >
                <ProjectForm
                    project={selectedProject}
                    onClose={handleProjectDrawerClose}
                    onSubmit={handleSubmit}
                />
            </Drawer>
        </Box>
    );
};

export default Projects;
